import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import Player from '@vimeo/player';
import { useOnScreen } from '@hooks/useOnScreen';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { useIsMounted } from '@hooks/useIsMounted';
import { StyledIframe, Wrapper, StyledCloudImage } from './VimeoVideo.styles';
import { addError } from '@utils/logger';
import { DexecureOptimizationMode } from '@utils/cloudAssetUrl.util';
import { FetchPriority } from '@components/CloudImage';

const ASPECT_RATIO = 16 / 9;

interface VimeoVideoProps {
  autoPause?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  embedUrl: string;
  autoPlay?: boolean;
  background?: boolean;
  muted?: boolean;
  loop?: boolean;
  isEagerLoading?: boolean;
  cover?: boolean;
  posterUrl?: string;
  posterOptimization?: DexecureOptimizationMode;
  title: string;
  aspectRatio?: number;
  alt: string;
  fetchPriority?: FetchPriority;
}

export const VimeoVideo: React.FC<VimeoVideoProps> = ({
  autoPause = true,
  controls = true,
  playsInline = true,
  embedUrl,
  autoPlay,
  background,
  muted,
  loop,
  isEagerLoading,
  cover,
  posterUrl,
  posterOptimization,
  title,
  aspectRatio = ASPECT_RATIO,
  alt = '',
  fetchPriority,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(containerRef, '0px', true);
  const isMounted = useIsMounted();
  const themeMediaQueryResult = useThemeMediaQuery('below_sm');
  // Media queries unavailable during static build; defaults to false (desktop) until hydration
  // Handle null case: if query result is null, default to desktop (false)
  const [isMobile, setIsMobile] = useState(false);

  // Update mobile state after mount to avoid hydration issues
  useEffect(() => {
    if (isMounted && themeMediaQueryResult !== null) {
      setIsMobile(themeMediaQueryResult === true);
    }
  }, [isMounted, themeMediaQueryResult]);

  const [isContainerGreaterThan16by9, setIsContainerGreaterThan16by9] = useState(false);
  const [hasError, setHasError] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const width = containerRef.current?.offsetWidth || 0;
      const height = containerRef.current?.offsetHeight || 0;

      setIsContainerGreaterThan16by9(width / height > aspectRatio);
    };

    measure();

    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [aspectRatio]);

  const src = useMemo(() => {
    const parsedUrl = queryString.parseUrl(embedUrl);
    const params = {
      ...parsedUrl.query,
      autopause: autoPause ? 1 : 0,
      autoplay: background ? 1 : autoPlay ? 1 : 0,
      background: background ? 1 : 0,
      muted: background ? 1 : muted ? 1 : 0,
      controls: background ? 0 : controls ? 1 : 0,
      playsinline: playsInline ? 1 : 0,
      loop: loop ? 1 : 0,
    };

    return queryString.stringifyUrl({ ...parsedUrl, query: params });
  }, [embedUrl, autoPause, autoPlay, background, muted, controls, playsInline, loop]);

  const playerId = useMemo(() => `vimeo-${src}`, [src]);

  const isVideoReady = useMemo(() => isEagerLoading || onScreen, [isEagerLoading, onScreen]);
  // Progressive loading: Start with poster for all devices, then load video on desktop after hydration
  // Gatsby does static builds, so we must never include the iframe in the static HTML
  // Otherwise mobile browsers will download Vimeo scripts even if we hide it after hydration
  // Strategy:
  // 1. Static build: Always show poster (never show video in static HTML)
  // 2. After hydration: Check if desktop, and if so, load video
  // 3. Mobile: Always show poster (never load video)
  const showVideo = useMemo(() => {
    if (hasError || !isVideoReady) return false;

    // Never show video in static build - always start with poster
    // This prevents iframe HTML from being included in static Gatsby build
    if (!isMounted) {
      return false;
    }

    // After hydration: only show video on desktop
    return !isMobile;
  }, [hasError, isVideoReady, isMounted, isMobile]);

  const handleOnError = () => {
    addError(
      {
        cause: 'Vimeo Video Iframe',
        name: 'MediaError',
        message: 'Failed to load vimeo video iframe',
      },
      {
        embedUrl,
        title,
        type: 'MediaError',
      }
    );
  };

  useEffect(() => {
    if (showVideo) {
      try {
        const player = new Player(playerId);
        player
          .ready()
          .then(() => {
            setHasError(false);
          })
          .catch((error: Error) => {
            setHasError(true);
            addError(error, {
              embedUrl,
              title,
              type: 'MediaError',
            });
          });
      } catch (error) {
        setHasError(true);
        addError(error as Error, {
          embedUrl,
          title,
          type: 'MediaError',
        });
      }
    }
  }, [showVideo, playerId, embedUrl, title]);

  return (
    <Wrapper ref={containerRef}>
      {/* Always show poster - it will be covered by video on desktop after hydration */}
      {posterUrl && (
        <StyledCloudImage
          height="100%"
          width="100%"
          src={posterUrl}
          optimization={posterOptimization}
          isEagerLoading={isEagerLoading}
          alt={alt}
          cover={cover}
          fetchPriority={fetchPriority}
        />
      )}
      {/* Only show video after hydration and on desktop */}
      {showVideo && (
        <StyledIframe
          id={playerId}
          data-vimeo-url={src}
          ref={iframeRef}
          isContainerGreaterThan16by9={isContainerGreaterThan16by9}
          cover={cover}
          src={src}
          frameBorder="0"
          allow="autoplay; fullscreen;"
          allowFullScreen
          width="100%"
          height="100%"
          title={title}
          onError={handleOnError}
        />
      )}
    </Wrapper>
  );
};
